import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Portal,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { masteryTone, orchard } from '../../theme';

const dayKey = (d) => new Date(d).toLocaleDateString('sv-SE');
function dayLabel(key, en) {
  const today = dayKey(new Date());
  const yest = dayKey(new Date(Date.now() - 864e5));
  if (key === today) return en ? 'Today' : 'Сегодня';
  if (key === yest) return en ? 'Yesterday' : 'Вчера';
  return new Date(`${key}T12:00:00`).toLocaleDateString(
    en ? 'en-GB' : 'ru-RU',
    { weekday: 'long', day: 'numeric', month: 'long' }
  );
}
const initials = (name) =>
  String(name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

/* «Недавно оценённые»: assessments done in the last days, newest first,
   with growth since the previous version — tick children and export their
   reports or compare them. Data: GET /api/analytics/recent. */
export default function RecentAssessments({ en, onExport, onCompare }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const [days, setDays] = useState(30);
  const [groupId, setGroupId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set()); // profileIds

  useEffect(() => {
    fetch('/api/analytics/groups', { credentials: 'include' })
      .then((r) => r.json())
      .then((g) => setGroups(Array.isArray(g) ? g : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError('');
    const q = new URLSearchParams({ days: String(days) });
    if (groupId) q.set('group', groupId);
    fetch(`/api/analytics/recent?${q}`, { credentials: 'include' })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.message || `HTTP ${r.status}`);
        return d;
      })
      .then((d) => !cancelled && setRows(d.rows || []))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [days, groupId]);

  const templates = useMemo(() => {
    const m = new Map();
    (rows || []).forEach((r) =>
      m.set(r.templateId, {
        _id: r.templateId,
        name: r.templateName,
        n: (m.get(r.templateId)?.n || 0) + 1,
      })
    );
    return [...m.values()].sort((a, b) => b.n - a.n);
  }, [rows]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rows || []).filter(
      (r) =>
        (!templateId || r.templateId === templateId) &&
        (!q || r.studentName.toLowerCase().includes(q))
    );
  }, [rows, templateId, query]);

  const byDay = useMemo(() => {
    const m = new Map();
    shown.forEach((r) => {
      const k = dayKey(r.assessedAt);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    });
    return [...m.entries()];
  }, [shown]);

  const toggle = (ids, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });

  const chosen = shown.filter((r) => selected.has(r.profileId));
  const chosenStudents = [...new Set(chosen.map((r) => r.studentId))];
  const allOn =
    shown.length > 0 && shown.every((r) => selected.has(r.profileId));

  const compareChosen = () => {
    const count = new Map();
    chosen.forEach((r) =>
      count.set(r.templateId, (count.get(r.templateId) || 0) + 1)
    );
    const tpl = [...count.entries()].sort((a, b) => b[1] - a[1])[0];
    onCompare(chosenStudents, tpl ? tpl[0] : '');
  };

  return (
    <Box sx={{ pb: chosen.length ? 10 : 0 }}>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, mb: 2 }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1.25,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(e, v) => v && setDays(v)}
          >
            <ToggleButton value={7}>{en ? '7 days' : '7 дней'}</ToggleButton>
            <ToggleButton value={30}>{en ? '30 days' : '30 дней'}</ToggleButton>
            <ToggleButton value={90}>
              {en ? '3 months' : '3 месяца'}
            </ToggleButton>
          </ToggleButtonGroup>
          <Select
            size="small"
            displayEmpty
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            sx={{ minWidth: 170, flex: '1 1 170px', maxWidth: 260 }}
          >
            <MenuItem value="">{en ? 'All groups' : 'Все группы'}</MenuItem>
            {groups.map((g) => (
              <MenuItem key={g._id} value={g._id}>
                {g.name}
                {g.mine ? '' : ` · ${g.teacher}`}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            displayEmpty
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            sx={{ minWidth: 170, flex: '1 1 170px', maxWidth: 280 }}
          >
            <MenuItem value="">{en ? 'All levels' : 'Все уровни'}</MenuItem>
            {templates.map((t) => (
              <MenuItem key={t._id} value={t._id}>
                {t.name} · {t.n}
              </MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={en ? 'Find a child…' : 'Найти ребёнка…'}
            sx={{
              flex: '1 1 160px',
              '& input': { fontSize: { xs: 16, sm: 14 } },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        {rows && rows.length > 0 && (
          <FormControlLabel
            sx={{ mt: 1, ml: 0 }}
            control={
              <Checkbox
                size="small"
                checked={allOn}
                indeterminate={!allOn && chosen.length > 0}
                onChange={(e) =>
                  toggle(
                    shown.map((r) => r.profileId),
                    e.target.checked
                  )
                }
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                {en
                  ? `Select all · ${shown.length} assessments`
                  : `Выбрать все · оценок: ${shown.length}`}
              </Typography>
            }
          />
        )}
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}
      {!rows && !error && <LinearProgress sx={{ borderRadius: 2 }} />}
      {rows && shown.length === 0 && (
        <Alert severity="info">
          {en ? 'No assessments in this period.' : 'За этот период оценок нет.'}
        </Alert>
      )}

      {byDay.map(([key, list]) => {
        const ids = list.map((r) => r.profileId);
        const dayOn = ids.every((id) => selected.has(id));
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
            >
              <Checkbox
                size="small"
                checked={dayOn}
                indeterminate={!dayOn && ids.some((id) => selected.has(id))}
                onChange={(e) => toggle(ids, e.target.checked)}
              />
              <Typography
                onClick={() => toggle(ids, !dayOn)}
                sx={{
                  fontWeight: 700,
                  textTransform: 'capitalize',
                  cursor: 'pointer',
                }}
              >
                {dayLabel(key, en)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                · {list.length}
              </Typography>
            </Box>
            <Paper
              variant="outlined"
              sx={{ borderRadius: 3, overflow: 'hidden' }}
            >
              {list.map((r, i) => {
                const on = selected.has(r.profileId);
                const delta =
                  r.prevPct == null || r.pct == null ? null : r.pct - r.prevPct;
                return (
                  <Box
                    key={r.profileId}
                    onClick={() => toggle([r.profileId], !on)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: { xs: 0.75, sm: 1.25 },
                      py: 1,
                      cursor: 'pointer',
                      borderTop: i ? '1px solid' : 'none',
                      borderColor: 'divider',
                      bgcolor: on
                        ? alpha(theme.palette.primary.main, 0.06)
                        : 'transparent',
                      '&:hover': {
                        bgcolor: on
                          ? alpha(theme.palette.primary.main, 0.09)
                          : 'action.hover',
                      },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={on}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => toggle([r.profileId], e.target.checked)}
                    />
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        fontSize: 13,
                        fontWeight: 700,
                        bgcolor: alpha(orchard.cherry, 0.12),
                        color: orchard.cherry,
                        display: { xs: 'none', sm: 'flex' },
                      }}
                    >
                      {initials(r.studentName)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        component={RouterLink}
                        to={`/student/${r.studentId}`}
                        onClick={(e) => e.stopPropagation()}
                        noWrap
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          fontSize: '0.93rem',
                          color: 'text.primary',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {r.studentName}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: 'block' }}
                      >
                        {r.templateName}
                        {r.version > 1
                          ? en
                            ? ` · v${r.version}`
                            : ` · версия ${r.version}`
                          : ''}
                        {` · ${r.units.length} ${en ? 'units' : r.units.length === 1 ? 'раздел' : r.units.length < 5 ? 'раздела' : 'разделов'}`}
                        {r.groups.length ? ` · ${r.groups.join(', ')}` : ''}
                      </Typography>
                    </Box>
                    {r.videos > 0 && (
                      <Tooltip
                        title={
                          en ? `${r.videos} video(s)` : `Видео: ${r.videos}`
                        }
                      >
                        <VideocamOutlinedIcon
                          fontSize="small"
                          sx={{ color: 'text.secondary' }}
                        />
                      </Tooltip>
                    )}
                    {delta != null && delta !== 0 && (
                      <Tooltip
                        title={
                          en ? `Previous: ${r.prevPct}%` : `Было: ${r.prevPct}%`
                        }
                      >
                        <Chip
                          size="small"
                          label={`${delta > 0 ? '+' : ''}${delta}`}
                          sx={{
                            fontWeight: 700,
                            bgcolor:
                              delta > 0
                                ? alpha(orchard.leaf, 0.14)
                                : alpha(orchard.cherry, 0.12),
                            color: delta > 0 ? orchard.leaf : orchard.cherry,
                          }}
                        />
                      </Tooltip>
                    )}
                    <Typography
                      sx={{
                        width: 46,
                        textAlign: 'right',
                        fontWeight: 800,
                        color:
                          r.pct == null
                            ? 'text.disabled'
                            : masteryTone(r.pct, true, dark),
                      }}
                    >
                      {r.pct == null ? '—' : `${r.pct}%`}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        width: 40,
                        textAlign: 'right',
                        display: { xs: 'none', sm: 'block' },
                      }}
                    >
                      {new Date(r.assessedAt).toLocaleTimeString(
                        en ? 'en-GB' : 'ru-RU',
                        { hour: '2-digit', minute: '2-digit' }
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          </Box>
        );
      })}

      {/* Portal: a transformed page wrapper would break position: fixed */}
      {chosen.length > 0 && (
        <Portal>
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: { xs: 76, md: 20 },
              zIndex: 1200,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: 999,
              maxWidth: 'calc(100vw - 24px)',
            }}
          >
            <Typography sx={{ fontWeight: 700, px: 0.5, whiteSpace: 'nowrap' }}>
              {en
                ? `${chosenStudents.length} selected`
                : `Выбрано: ${chosenStudents.length}`}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => onExport(chosenStudents)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {en ? 'Reports' : 'Отчёты'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CompareArrowsIcon />}
              disabled={chosenStudents.length < 2}
              onClick={compareChosen}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {en ? 'Compare' : 'Сравнить'}
            </Button>
            <IconButton
              size="small"
              onClick={() => setSelected(new Set())}
              aria-label={en ? 'Clear' : 'Снять выбор'}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>
        </Portal>
      )}
    </Box>
  );
}
